/**
 * Threedium homepage prompt-bar → agents handoff.
 *
 * The homepage OAuth callback may carry extra query params besides
 * user_app_id / otp. These helpers map those params, persist them across
 * the callback → /dashboard → /experience/:id remount (MainAppGate key), and keep the
 * payload until the workspace actually starts the first run.
 */

export const THREEDIUM_DEEPLINK_STORAGE_KEY = 'threedium_deeplink_prompt';

const PROJECT_TYPE_TO_TEMPLATE = {
  r3f: 'r3f',
  three_js: 'r3f',
  react_three_fiber_project: 'r3f',
};

const TGP_AI_PATTERN = /^TGP(?:_(high|mid|low))?$/i;

export function mapProjectTypeToTemplate(projectType) {
  if (!projectType) return null;
  return PROJECT_TYPE_TO_TEMPLATE[String(projectType).trim()] || null;
}

export function isTgpSelectedAi(selectedAi) {
  return TGP_AI_PATTERN.test(String(selectedAi || '').trim());
}

export function isDeepLinkExpired(expiry) {
  const expiryMs = Number(expiry);
  if (!expiry || Number.isNaN(expiryMs)) return false;
  return Date.now() > expiryMs;
}

export function parseFromSearchParams(searchParams) {
  if (!searchParams || typeof searchParams.get !== 'function') return null;
  const inputText = searchParams.get('input_text');
  const selectedAi = searchParams.get('selected_ai');
  const projectType = searchParams.get('project_type');
  const expiry = searchParams.get('expiry');
  if (!inputText && !selectedAi && !projectType) return null;
  return {
    inputText: inputText || '',
    selectedAi: selectedAi || null,
    projectType: projectType || null,
    expiry: expiry || null,
    createdProjectId: null,
  };
}

export function isSupportedHandoff(payload) {
  if (!payload) return false;
  return Boolean(
    mapProjectTypeToTemplate(payload.projectType) && isTgpSelectedAi(payload.selectedAi)
  );
}

function readRaw() {
  try {
    return sessionStorage.getItem(THREEDIUM_DEEPLINK_STORAGE_KEY);
  } catch {
    return null;
  }
}

function readParsed() {
  const raw = readRaw();
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writePayload(payload) {
  try {
    sessionStorage.setItem(THREEDIUM_DEEPLINK_STORAGE_KEY, JSON.stringify(payload));
    return true;
  } catch (err) {
    console.warn('Failed to stash Threedium deep-link params:', err);
    return false;
  }
}

export function stashThreediumDeepLink(payload) {
  if (!payload) return;
  writePayload({
    inputText: payload.inputText || '',
    selectedAi: payload.selectedAi || null,
    projectType: payload.projectType || null,
    expiry: payload.expiry || null,
    createdProjectId: payload.createdProjectId || null,
    stashedAt: Date.now(),
  });
}

export function peekThreediumDeepLink() {
  const payload = readParsed();
  if (!payload) return null;
  // Expiry only blocks before we create a project. After that the user may
  // still be waiting for ECS and we must keep the prompt.
  if (isDeepLinkExpired(payload.expiry) && !payload.createdProjectId) return null;
  return payload;
}

export function setCreatedProjectId(projectId) {
  const payload = readParsed();
  if (!payload || !projectId) return;
  writePayload({ ...payload, createdProjectId: String(projectId) });
}

export function consumeThreediumDeepLink() {
  const payload = peekThreediumDeepLink();
  try {
    sessionStorage.removeItem(THREEDIUM_DEEPLINK_STORAGE_KEY);
  } catch {
    // ignore
  }
  return payload;
}
